import NotFoundPage from "@saleor/components/NotFoundPage";
import { WindowTitle } from "@saleor/components/WindowTitle";
import { DEFAULT_INITIAL_SEARCH_DATA } from "@saleor/config";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import useShop from "@saleor/hooks/useShop";
import useUser from "@saleor/hooks/useUser";
import { commonMessages } from "@saleor/intl";
import useBrandPrivateSearch from "@saleor/searches/useBrandPrivateSearch";
import createMetadataCreateHandler from "@saleor/utils/handlers/metadataCreateHandler";
import {
  useMetadataUpdate,
  usePrivateMetadataUpdate
} from "@saleor/utils/metadata/updateMetadata";
import { useWarehouseList } from "@saleor/warehouses/queries";
import { warehouseAddPath } from "@saleor/warehouses/urls";
import React from "react";
import { useIntl } from "react-intl";

import { useAuth } from "../../auth/AuthProvider";
import { decimal, maybe, stringToFloat } from "../../misc";
import { StoreTypeEnum } from "../../types/globalTypes";
import ProductVariantCreatePage from "../components/ProductVariantCreatePage";
import { ProductVariantCreateData } from "../components/ProductVariantCreatePage/form";
import useBrandUpdate from "../hooks/useBrandUpdate";
import useProductUserType from "../hooks/useProductUserType";
import useUserViewUpdate from "../hooks/useUserViewUpdate";
import {
  useProductVariantReorderMutation,
  useVariantCreateMutation
} from "../mutations";
import { useProductVariantCreateQuery } from "../queries";
import {
  KAMSQueryParams,
  productListUrl,
  productUrl,
  productVariantEditUrl
} from "../urls";
import {
  hasNoError,
  useProductVariantValidation
} from "../utils/productValidations";
import { createVariantReorderHandler } from "./ProductUpdate/handlers";

interface ProductVariantCreateProps {
  productId: string;
  params?: KAMSQueryParams;
}

export const ProductVariant: React.FC<ProductVariantCreateProps> = ({
  productId,
  params
}) => {
  const {
    user: { userType }
  } = useAuth();
  const navigate = useNavigator();
  const notify = useNotifier();
  const shop = useShop();
  const intl = useIntl();
  const { user: userDetail } = useUser();
  const [validate, setValidator] = React.useState({
    price: null,
    height: null,
    length: null,
    sku: null,
    weight: null,
    width: null
  });

  // Custom hook for determining if user is KAMS, superuser or regular user
  const productsUser = useProductUserType(params);

  // Hook for updating selected KAMS user view
  const [onUserViewChange, userView, userViews] = useUserViewUpdate(
    userDetail,
    params
  );

  const { result: brandsResult } = useBrandPrivateSearch({
    skip: !productsUser.isKAMS,
    variables: {
      ...DEFAULT_INITIAL_SEARCH_DATA,
      accountManager: productsUser.id
    }
  });

  const brands = maybe(() => brandsResult?.data?.search?.edges, []).map(
    edge => edge.node
  );

  // Hook for updating of brands
  const [onBrandChange, brand, brandChoices] = useBrandUpdate(
    params,
    brands,
    brandsResult.loading
  );

  const userAccessTypes = userType.accesses.length
    ? userType.accesses.map(({ storeType }) => storeType)
    : [];

  const isTrackInventory = userAccessTypes.includes(
    StoreTypeEnum.GENERAL_MERCHANDISE
  )
    ? true
    : false;

  const warehouses = useWarehouseList({
    displayLoader: true,
    variables: {
      first: 50
    }
  });

  const { data, loading: productLoading } = useProductVariantCreateQuery({
    displayLoader: true,
    variables: { id: productId }
  });

  const [variantCreate, variantCreateResult] = useVariantCreateMutation({
    onCompleted: data => {
      if (data.productVariantCreate.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage(commonMessages.savedChanges)
        });
        navigate(
          productVariantEditUrl(
            productId,
            data.productVariantCreate.productVariant.id
          )
        );
      }
    }
  });
  const [updateMetadata] = useMetadataUpdate({});
  const [updatePrivateMetadata] = usePrivateMetadataUpdate({});

  const product = data?.product;

  if (product === null) {
    return <NotFoundPage onBack={() => navigate(productListUrl())} />;
  }

  const [
    reorderProductVariants,
    reorderProductVariantsOpts
  ] = useProductVariantReorderMutation({});

  const handleVariantReorder = createVariantReorderHandler(product, variables =>
    reorderProductVariants({ variables })
  );

  const handleBack = () => navigate(productUrl(productId));
  const handleCreate = async (formData: ProductVariantCreateData) => {
    const result = await variantCreate({
      variables: {
        input: {
          attributes: formData.attributes
            .filter(attribute => attribute.value !== "")
            .map(attribute => ({
              id: attribute.id,
              values: [attribute.value]
            })),
          ...(productsUser.isKAMS && {
            brand: formData.brand
          }),
          costPrice: decimal(formData.costPrice),
          height: formData.height === "" ? null : formData.height,
          length: formData.length === "" ? null : formData.length,
          price: decimal(formData.price),
          product: productId,
          sku: formData.sku,
          stocks: formData.stocks.map(stock => ({
            quantity: parseInt(stock.value, 10) || 0,
            warehouse: stock.id
          })),
          trackInventory: formData.trackInventory || false,
          weight: stringToFloat(formData.weight),
          width: formData.width === "" ? null : formData.width
        }
      }
    });

    return result.data.productVariantCreate?.productVariant?.id || null;
  };

  const handleValidation = data => {
    const validationResult = useProductVariantValidation(data);
    setValidator(validationResult);
    if (hasNoError(validationResult)) {
      handleSubmit(data);
    }
  };

  const handleSubmit = createMetadataCreateHandler(
    handleCreate,
    updateMetadata,
    updatePrivateMetadata
  );
  const handleVariantClick = (id: string) =>
    navigate(productVariantEditUrl(productId, id));

  const disableForm =
    productLoading ||
    variantCreateResult.loading ||
    reorderProductVariantsOpts.loading;

  return (
    <>
      <WindowTitle
        title={intl.formatMessage({
          defaultMessage: "Create variant",
          description: "window title"
        })}
      />
      <ProductVariantCreatePage
        productsUser={productsUser}
        brand={brand.value}
        brands={brandChoices}
        brandLabel={brand.label}
        onBrandChange={onBrandChange}
        userView={userView.value}
        userViewLabel={userView.label}
        userViews={userViews}
        onUserViewChange={onUserViewChange}
        currencySymbol={shop?.defaultCurrency}
        disabled={disableForm}
        errors={variantCreateResult.data?.productVariantCreate.errors || []}
        header={intl.formatMessage({
          defaultMessage: "Create Variant",
          description: "header"
        })}
        product={data?.product}
        onBack={handleBack}
        onVariantClick={handleVariantClick}
        onWarehouseConfigure={() => navigate(warehouseAddPath)}
        onVariantReorder={handleVariantReorder}
        saveButtonBarState={variantCreateResult.status}
        warehouses={
          warehouses.data?.warehouses.edges.map(edge => edge.node) || []
        }
        weightUnit={shop?.defaultWeightUnit}
        isTrackInventory={isTrackInventory}
        onSubmit={handleValidation}
        validate={validate}
      />
    </>
  );
};
export default ProductVariant;
